import React, { useEffect, useState } from 'react';
import './App.css';
import { getClientRoute } from './utils/appRoute';
import Header from './components/layout/Header';
import Hero from './components/sections/Hero';
import About from './components/sections/About';
import Services from './components/sections/Services';
import Technologies from './components/sections/Technologies';
import Contact from './components/sections/Contact';
import Footer from './components/layout/Footer';
import Products from './components/sections/Products';
import {
  PrivacyPolicyPage,
  RefundPolicyPage,
  ShippingPolicyPage,
  TermsPage
} from './components/legal/LegalPages';
import AboutPage from './components/pages/AboutPage';

/** Scroll to #section when opening the home page with a hash (e.g. from /about via nav). */
function useScrollToHashOnHomeMount() {
  useEffect(() => {
    const id = window.location.hash.replace(/^#/, '').trim();
    if (!id) return undefined;

    const root = document.documentElement;
    const previousInline = root.style.scrollBehavior;

    const restore = () => {
      root.style.scrollBehavior = previousInline;
    };

    let restored = false;
    const finish = () => {
      if (restored) return;
      restored = true;
      restore();
    };

    const attempt = (): boolean => {
      const el = document.getElementById(decodeURIComponent(id));
      if (!el) return false;
      // html { scroll-behavior: smooth } in index.css would still animate; override for this jump only
      root.style.scrollBehavior = 'auto';
      el.scrollIntoView({ block: 'start', inline: 'nearest' });
      return true;
    };

    if (attempt()) {
      requestAnimationFrame(finish);
      return () => finish();
    }

    root.style.scrollBehavior = 'auto';
    const t0 = window.setTimeout(() => {
      if (attempt()) finish();
    }, 0);
    const t1 = window.setTimeout(() => {
      if (attempt()) finish();
    }, 120);
    const t2 = window.setTimeout(() => {
      if (attempt()) finish();
    }, 350);
    const tEnd = window.setTimeout(finish, 450);

    return () => {
      window.clearTimeout(t0);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(tEnd);
      finish();
    };
  }, []);
}

function HomeLayout() {
  useScrollToHashOnHomeMount();

  return (
    <>
      <Header />
      <main>
        <Hero />
        <About />
        <Services />
        <Technologies />
        {/* <Team /> */}
        <Products />
        <Contact />
      </main>
      <Footer />
    </>
  );
}

function scrollWindowToTop() {
  const root = document.documentElement;
  const prevBehavior = root.style.scrollBehavior;
  root.style.scrollBehavior = 'auto';
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  window.scrollTo(0, 0);
  root.style.scrollBehavior = prevBehavior;
}

/**
 * Scroll to top when the “app path” changes (hash routes like #/about, or pathname).
 * Skip on home when the hash is a section anchor only (#contact), so HomeLayout can scroll to the section.
 */
function useScrollToTopOnRoute(path: string) {
  useEffect(() => {
    const hash = window.location.hash;
    const isSectionOnlyHash = hash.length > 1 && !hash.startsWith('#/');
    if (path === '/' && isSectionOnlyHash) {
      return undefined;
    }

    const run = () => scrollWindowToTop();
    run();
    let innerRaf = 0;
    const outerRaf = requestAnimationFrame(() => {
      run();
      innerRaf = requestAnimationFrame(run);
    });
    const t = window.setTimeout(run, 0);
    const t2 = window.setTimeout(run, 50);
    return () => {
      cancelAnimationFrame(outerRaf);
      cancelAnimationFrame(innerRaf);
      window.clearTimeout(t);
      window.clearTimeout(t2);
    };
  }, [path]);
}

function App() {
  const [, setRouteTick] = useState(0);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    const onHashChange = () => {
      setRouteTick((t) => t + 1);
      const h = window.location.hash;
      if (h.startsWith('#/')) {
        queueMicrotask(() => {
          scrollWindowToTop();
          requestAnimationFrame(() => {
            scrollWindowToTop();
            requestAnimationFrame(scrollWindowToTop);
          });
        });
      }
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const path = getClientRoute();
  useScrollToTopOnRoute(path);

  if (path === '/privacy-policy') {
    return (
      <div className="App">
        <Header />
        <main>
          <PrivacyPolicyPage />
        </main>
        <Footer />
      </div>
    );
  }

  if (path === '/terms-and-conditions') {
    return (
      <div className="App">
        <Header />
        <main>
          <TermsPage />
        </main>
        <Footer />
      </div>
    );
  }

  if (path === '/refund-policy') {
    return (
      <div className="App">
        <Header />
        <main>
          <RefundPolicyPage />
        </main>
        <Footer />
      </div>
    );
  }

  if (path === '/shipping-policy') {
    return (
      <div className="App">
        <Header />
        <main>
          <ShippingPolicyPage />
        </main>
        <Footer />
      </div>
    );
  }

  if (path === '/about') {
    return (
      <div className="App">
        <Header />
        <main>
          <AboutPage />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <HomeLayout />
    </div>
  );
}

export default App;
